import { RequestStatus } from '../../store/network/managedRequest';
import {
    GetVipUsersInLaundryRoom,
    RemoveVipUsersFromLaundryRoom,
    VipUser,
    VipUsersForRemovalRequest,
    VipUsersInLaundryRoomDto,
    VipUsersRemovalReportDto,
} from '../../store/vipUsersSlice';
import { connect } from 'react-redux';
import { RootState } from '../../store';
import React, { useEffect } from 'react';
import EditLargeCardFormik, {
    EditLargeCardFormikProps,
} from '../main/edit-large-card-formik';
import Checkbox from '../main/checkbox';
import useTranslate from '../useTranslate';
import { PbGenericRequest } from '../../store/pbRequestSlice';
import { PbRequest } from '../../store/pbRequest';

const MAX_NUMBER_IN_PREVIEW = 5;

interface Props {
    laundryRoomId: number;
    GetVipUsersInLaundryRoom: (locationId: number) => void;
    RemoveVipUsersFromLaundryRoom: (
        locationId: number,
        vipUsersForRemoval: VipUsersForRemovalRequest
    ) => void;
    getVipUsersInLaundryRoomRequest: PbGenericRequest;
    getVipUsersInLaundryRoomData?: VipUsersInLaundryRoomDto;
    deleteVipUsersInLaundryRoomRequest: PbGenericRequest;
    deleteVipUsersInLaundryRoomData?: VipUsersRemovalReportDto;
}

interface VipUsersForRemovingFormValues {
    users: string[];
}

function VipRemoveUserCard(props: Readonly<Props>) {
    const strings = useTranslate('VipRemoveUserCard');

    useEffect(() => {
        if (!props.laundryRoomId) {
            return;
        }
        if (
            [RequestStatus.INITIAL, RequestStatus.SUCCESS].includes(
                props.deleteVipUsersInLaundryRoomRequest.status
            )
        ) {
            props.GetVipUsersInLaundryRoom(props.laundryRoomId);
        }
    }, [props.laundryRoomId, props.deleteVipUsersInLaundryRoomRequest.status]);

    if (!props.laundryRoomId) {
        return null;
    }

    if (
        !props.getVipUsersInLaundryRoomData ||
        [RequestStatus.INITIAL, RequestStatus.PENDING].includes(
            props.getVipUsersInLaundryRoomRequest.status
        )
    ) {
        return <p>{strings.loading}</p>;
    }

    if (
        props.getVipUsersInLaundryRoomRequest.status === RequestStatus.FAILURE
    ) {
        return <p>{strings.errorLoading}</p>;
    }
    const usersFromBackend = props.getVipUsersInLaundryRoomData;

    const initialValues = {
        users: [],
    };

    function handleSubmit(values: VipUsersForRemovingFormValues) {
        props.RemoveVipUsersFromLaundryRoom(props.laundryRoomId, {
            vip_user_ids: values.users.map(Number),
        });
    }

    function renderEditState(
        formProps: EditLargeCardFormikProps<VipUsersForRemovingFormValues>
    ) {
        const count = formProps.values.users.length;

        return (
            <div role="group" aria-labelledby="checkbox-group">
                {usersFromBackend.vip_users?.map((user) => (
                    <Checkbox
                        key={user.user_id}
                        name="users"
                        value={user.user_id}
                        label={` ${getUserLabel(user)}`}
                    />
                ))}
                {strings._utils.sprintf(strings.selectedCount, count)}
            </div>
        );
    }

    function renderReadOnlyState() {
        if (!usersFromBackend.vip_users) {
            return null;
        }
        if (usersFromBackend.vip_users.length === 0) {
            return <div>{strings.noUsers}</div>;
        }

        if (usersFromBackend.vip_users.length > MAX_NUMBER_IN_PREVIEW + 1) {
            return [
                ...usersFromBackend.vip_users
                    .slice(0, MAX_NUMBER_IN_PREVIEW - 1)
                    .map((user) => (
                        <div key={user.user_id}>{getUserLabel(user)}</div>
                    )),
                <div key={'extras'}>
                    {strings._utils.sprintf(
                        strings.listHasMore,
                        usersFromBackend.vip_users.length -
                            MAX_NUMBER_IN_PREVIEW
                    )}
                </div>,
            ];
        }

        return usersFromBackend.vip_users?.map((user) => (
            <div key={user.user_id}>{getUserLabel(user)}</div>
        ));
    }

    function getUserLabel(user: VipUser) {
        return user.customer_id;
    }

    function renderForm(
        formProps: EditLargeCardFormikProps<VipUsersForRemovingFormValues>
    ) {
        if (formProps.active) {
            return renderEditState(formProps);
        } else {
            return renderReadOnlyState();
        }
    }

    return (
        <EditLargeCardFormik
            title={strings.title}
            description={strings.description}
            handleSubmit={handleSubmit}
            initialValues={initialValues}
            error={false}
            children={renderForm}
            submitLabel={strings.removeAction}
        />
    );
}

export default connect(
    (store: RootState) => ({
        getVipUsersInLaundryRoomRequest:
            store.pbRequest[PbRequest.GET_VIP_USERS_IN_LAUNDRY_ROOM],
        getVipUsersInLaundryRoomData: store.vipUsers.vipUsersInLaundryRoom,
        deleteVipUsersInLaundryRoomRequest:
            store.pbRequest[PbRequest.DELETE_VIP_USERS_IN_LAUNDRY_ROOM],
        deleteVipUsersInLaundryRoomData:
            store.vipUsers.deleteVipUsersInLaundryRoomReport,
    }),
    { GetVipUsersInLaundryRoom, RemoveVipUsersFromLaundryRoom }
)(VipRemoveUserCard);
